import React, {useCallback, useState} from 'react';
import styled from 'styled-components';
import {Z_INDEXES} from '../../../constants/layers';
import {useIsomorphicLayoutEffect} from '../../../hooks/useIsomorphicLayoutEffect/useIsomorphicLayoutEffect';
import {RenderInPortal} from '../RenderInPortal/RenderInPortal';
import {Toast} from './Toast';
import {ToastProps as BaseToastProps} from './types';

type ToastProps = Omit<BaseToastProps, 'animationDuration'>;

interface ToastPropsWithUUI extends ToastProps {
  uuid: number;
}

interface ContainerProps {
  maxToasts?: number;
}

// Custom event names
const TOAST_SHOW_EVENT = 'toast:show';
const TOAST_REMOVE_ALL_EVENT = 'toast:remove-all';

/**
 * Dispatch a custom event to show a toast.
 * Works independently of React context — the ToastContainer listens for these events.
 */
export const showToast = (toast: ToastProps): void => {
  document.dispatchEvent(new CustomEvent(TOAST_SHOW_EVENT, {detail: toast}));
};

/**
 * Dispatch a custom event to remove all visible toasts.
 */
export const removeAllToasts = (): void => {
  document.dispatchEvent(new Event(TOAST_REMOVE_ALL_EVENT));
};

/**
 * Standalone toast renderer that listens for custom events.
 * Can be lazy-loaded and rendered as a sibling to the main app tree.
 *
 * Uses useLayoutEffect for event listener registration so that listeners
 * are active before any useEffect in sibling components fires showToast.
 */
export const ToastContainer: React.FC<ContainerProps> = ({maxToasts}) => {
  const [toasts, setToasts] = useState<ToastPropsWithUUI[]>([]);

  useIsomorphicLayoutEffect(() => {
    const handleShowToast = (event: Event) => {
      const toastProps = (event as CustomEvent<ToastProps>).detail;
      setToasts(prev => {
        const updatedToasts =
          maxToasts && prev.length + 1 > maxToasts ? prev.slice(1) : prev;
        return [...updatedToasts, {...toastProps, uuid: performance.now()}];
      });
    };

    const handleRemoveAll = () => {
      setToasts([]);
    };

    document.addEventListener(TOAST_SHOW_EVENT, handleShowToast);
    document.addEventListener(TOAST_REMOVE_ALL_EVENT, handleRemoveAll);

    return () => {
      document.removeEventListener(TOAST_SHOW_EVENT, handleShowToast);
      document.removeEventListener(TOAST_REMOVE_ALL_EVENT, handleRemoveAll);
    };
  }, [maxToasts]);

  const removeToast = useCallback((toastUuid: number) => {
    setToasts(prev => prev.filter(toast => toast.uuid !== toastUuid));
  }, []);

  return (
    <RenderInPortal portalId="toast-portal-host">
      <ToastsContainer>
        {toasts.map(toast => (
          <Toast
            {...toast}
            key={toast.uuid}
            onDismiss={() => {
              toast.onDismiss?.();
              removeToast(toast.uuid);
            }}
            onAutoClose={() => {
              toast.onAutoClose?.();
              removeToast(toast.uuid);
            }}
          />
        ))}
      </ToastsContainer>
    </RenderInPortal>
  );
};

const ToastsContainer = styled.div`
  z-index: ${Z_INDEXES.TADARIDA_TOAST_CONTAINER};
  position: absolute;
`;
